'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Loader2, Eye, EyeOff, Moon, Stars } from 'lucide-react';
import { toast } from 'sonner';
import { signIn, signInWithRedirect } from 'aws-amplify/auth';
import { useAuth } from '@/contexts/AuthContext';
import ExperienceAtmosphere from '@/components/ui/ExperienceAtmosphere';
import ThemeToggle from '@/components/ui/ThemeToggle';

const getAuthErrorMessage = (error: unknown) => {
    if (error instanceof Error) return error.message;
    return '';
};

export default function LoginPage() {
    const { isLoading: authLoading, isAuthenticated } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const { isSignedIn, nextStep } = await signIn({
                username: formData.email,
                password: formData.password,
            });

            if (isSignedIn) {
                toast.success('Welcome back to your brief space.');
                window.location.href = '/dashboard';
            } else {
                console.info('Next step required:', nextStep);
                if (nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
                    window.location.href = `/signup?confirm=1&username=${encodeURIComponent(formData.email)}`;
                }
            }
        } catch (error: unknown) {
            console.error(error);
            const errorMessage = getAuthErrorMessage(error);
            // Handle already signed in case
            if (
                error instanceof Error &&
                (error.name === 'UserAlreadyAuthenticatedException' || error.message.includes('already a signed in user'))
            ) {
                window.location.href = '/dashboard';
                return;
            }
            toast.error(errorMessage || "Invalid credentials");
        } finally {
            setIsLoading(false);
        }
    };

    // Show loading while checking auth
    if (authLoading) {
        return (
            <div className="min-h-screen site-shell auth-shell flex items-center justify-center">
                <Loader2 className="animate-spin text-amber-400" size={48} />
            </div>
        );
    }

    // Already authenticated - show link to dashboard instead of redirecting
    if (isAuthenticated) {
        return (
            <div className="min-h-screen site-shell auth-shell flex items-center justify-center p-4">
                <div className="card-feature w-full max-w-md p-8 text-center">
                    <div className="w-20 h-20 rounded-full manuscript-panel border border-accent/20 flex items-center justify-center mx-auto mb-6">
                        <Sparkles className="text-white" size={36} />
                    </div>
                    <h1 className="text-2xl font-bold text-[#f7ead2] mb-4 font-playfair">
                        You&apos;re Already Signed In!
                    </h1>
                    <p className="text-[#ddceb0]/68 mb-8">
                        Your dashboard and saved brief history are ready when you are.
                    </p>
                    <Link
                        href="/dashboard"
                        className="inline-flex items-center gap-2 manuscript-button text-[#1b160f] font-bold py-3 px-6 rounded-xl transition-opacity"
                    >
                        <ArrowRight size={18} />
                        Go to Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen site-shell auth-shell text-white flex items-center justify-center p-4 pt-20 sm:pt-4 relative overflow-hidden">
            <ExperienceAtmosphere variant="auth" priority />
            <div className="absolute right-5 top-5 z-20"><ThemeToggle /></div>

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="w-full max-w-md relative z-10"
            >
                {/* Logo & Header */}
                <div className="text-center mb-8">
                    <Link href="/" className="inline-flex items-center gap-3 mb-6 group">
                        <div className="card-icon card-icon-lg">
                            <Sparkles className="text-accent" size={24} />
                        </div>
                        <span className="text-2xl font-bold tracking-widest font-playfair uppercase text-[#f7ead2] group-hover:text-amber-400 transition-colors">YourAura</span>
                    </Link>

                    <motion.h1
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="mb-3 font-playfair text-4xl font-bold text-foreground"
                    >
                        Welcome Back
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="text-[#ead8b7]/88 text-sm flex items-center justify-center gap-2"
                    >
                        <Moon size={14} className="text-indigo-400" />
                        Return to your daily brief, saved context, and guidance history
                        <Stars size={14} className="text-amber-400" />
                    </motion.p>
                </div>

                {/* Login Card */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 }}
                    className="relative"
                >
                    <div className="card-feature p-6 sm:p-8">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <label htmlFor="login-email" className="text-xs uppercase tracking-[0.22em] text-[#ead8b7]/88 pl-1 flex items-center gap-2">
                                    <span className="w-1 h-1 rounded-full bg-accent" />
                                    Email Address
                                </label>
                                <input
                                    id="login-email"
                                    autoComplete="email"
                                    type="email"
                                    required
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full manuscript-input rounded-xl px-4 py-4 focus:outline-none focus:border-accent/40 focus:ring-2 focus:ring-accent/20 transition-all text-[#f7ead2] placeholder:text-[#d8c59f]/35"
                                    placeholder="you@example.com"
                                />
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="login-password" className="text-xs uppercase tracking-[0.22em] text-[#ead8b7]/88 pl-1 flex items-center gap-2">
                                    <span className="w-1 h-1 rounded-full bg-accent" />
                                    Password
                                </label>
                                <div className="relative">
                                    <input
                                        id="login-password"
                                        autoComplete="current-password"
                                        type={showPassword ? "text" : "password"}
                                        required
                                        value={formData.password}
                                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                                        className="w-full manuscript-input rounded-xl px-4 py-4 pr-12 focus:outline-none focus:border-accent/40 focus:ring-2 focus:ring-accent/20 transition-all text-[#f7ead2] placeholder:text-[#d8c59f]/35"
                                        placeholder="••••••••••••"
                                    />
                                    <button
                                        type="button"
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#d8c59f]/70 hover:text-[#f7ead2] transition-colors"
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            {/* Forgot Password Link */}
                            <div className="text-right">
                                <Link
                                    href="/forgot-password"
                                    className="text-xs text-[#ead8b7]/80 hover:text-amber-400 transition-colors"
                                >
                                    Forgot your password?
                                </Link>
                            </div>

                            <motion.button
                                type="submit"
                                disabled={isLoading}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="w-full manuscript-button bg-[length:200%_100%] hover:bg-right text-[#1b160f] font-bold py-4 rounded-xl shadow-lg shadow-amber-500/20 flex items-center justify-center gap-3 transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group"
                            >
                                {isLoading ? (
                                    <Loader2 className="animate-spin" size={20} />
                                ) : (
                                    <>
                                        <span>Open My Brief Space</span>
                                        <ArrowRight size={18} />
                                    </>
                                )}
                            </motion.button>
                        </form>

                        <div className="mt-8 pt-6 border-t border-[#e8d7b0]/10 text-center">
                            <p className="text-sm text-[#ead8b7]/74">
                                New here?{' '}
                                <Link href="/signup" className="text-accent hover:text-[#f7ead2] font-bold transition-colors hover:underline underline-offset-4">
                                    Create Free Account
                                </Link>
                            </p>
                        </div>

                        <div className="flex items-center gap-4 mt-6">
                            <div className="flex-1 h-px bg-[#e8d7b0]/10" />
                            <span className="text-xs text-[#ead8b7]/58 uppercase tracking-[0.2em]">or</span>
                            <div className="flex-1 h-px bg-[#e8d7b0]/10" />
                        </div>

                        {/* Google Sign In */}
                        <motion.button
                            type="button"
                            onClick={async () => {
                                try {
                                    await signInWithRedirect({ provider: 'Google' });
                                } catch (error: unknown) {
                                    console.error(error);
                                    const errorMessage = getAuthErrorMessage(error);
                                    // Handle already signed in case for Google Auth too
                                    if (
                                        error instanceof Error &&
                                        (
                                            error.name === 'UserAlreadyAuthenticatedException' ||
                                            error.message.includes('already a signed in user') ||
                                            error.message.includes('already authenticated')
                                        )
                                    ) {
                                        window.location.href = '/dashboard';
                                        return;
                                    }
                                    toast.error(errorMessage || "Google sign-in failed");
                                }
                            }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="w-full mt-4 manuscript-button-secondary rounded-xl py-4 px-6 flex items-center justify-center gap-3 transition-all"
                        >
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                            <span className="text-[#f7ead2] font-medium">Continue with Google</span>
                        </motion.button>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="mt-8 text-center"
                >
                    <p className="text-[10px] text-[#ead8b7]/52 tracking-[0.22em] uppercase">
                        Private, encrypted, and built for repeat use
                    </p>
                </motion.div>
            </motion.div>
        </div>
    );
}
